import { useMe } from "../../app/hooks/useSession.js";
import { useFleet, type VehicleView, type FleetView } from "../../app/hooks/useBuses.js";
import { orderCoaches } from "./fleetOrder.js";
import { bunkSide, bunkGroup, bunkTier, SIDE_LABEL, type Side, type Group, type Tier } from "./bunkGeometry.js";
import { CoachFacts } from "./MyBunkCard.js";
import { BusStockPanel } from "./BusStockPanel.js";
import styles from "./FleetPager.module.scss";

/**
 * ARTIST CNTRL PRO — the whole-fleet pager on the Day of Show bus sheet (ART-148). One page per COACH (trucks
 * have no bunks and no riders), swiped left-to-right, the caller's own coach first so the sheet opens on the page
 * that matters to them. Each page is the coach's facts plus the FULL bunk chart — who sleeps where — which is
 * why it is AV-or-TM only: the per-seat visibility ruling (PO, 2026-09-12) gives that chart to AV + TM and no one
 * else. The caller decides from the SERVED `canEditFleet` verdict; this component additionally reads the fleet
 * through `GET /buses`, which 403s anyone else, so there is no client-only gate here.
 *
 * Legacy shipped this TM-only ("the TM role should be able to swipe left to right and see all the buses"); the
 * ART-148 ruling widened it to the AV seats, the exact audience that already sees this chart on desktop.
 */
export function FleetPager() {
  const fleet = useFleet();
  const me = useMe();
  if (fleet.isLoading) return <p className={styles.note}>Loading the fleet…</p>;
  if (fleet.isError || !fleet.data) return <p className={styles.note}>Couldn't load the fleet. Try again in a moment.</p>;
  const myId = me.data?.person?.personId ?? null;
  const coaches = orderCoaches(fleet.data, myId);
  if (coaches.length === 0) return <p className={styles.note}>No buses in the fleet yet — add one from the Buses screen.</p>;
  return <Pages coaches={coaches} crew={fleet.data.crew} myId={myId} />;
}

function Pages({ coaches, crew, myId }: { coaches: VehicleView[]; crew: FleetView["crew"]; myId: string | null }) {
  const nameOf = new Map(crew.map((c) => [c.personId, c.displayName]));
  return (
    <div className={styles.pager} role="region" aria-label={coaches.length > 1 ? `${coaches.length} buses — swipe` : "Bus"}>
      {coaches.map((v, i) => (
        <section
          key={v.vehicleId}
          className={styles.page}
          aria-label={`${v.name}, ${i + 1} of ${coaches.length}`}
        >
          <header className={styles.pageHead}>
            <span className={styles.pageName}>{v.name}</span>
            {v.tag ? <span className={styles.pageTag}>{v.tag}</span> : null}
            <span className={styles.pageCount}>{v.filled}/{v.capacity}</span>
          </header>
          <CoachFacts coach={v} />
          <BunkChart v={v} nameOf={nameOf} myId={myId} />
          <BusStockPanel vehicleId={v.vehicleId} compact />
        </section>
      ))}
    </div>
  );
}

const SIDES: Side[] = ["D", "P"];
const GROUPS: Group[] = ["F", "B"];
const TIERS: Tier[] = ["T", "M", "B"];

/** The printed-bunk-chart layout: one column per side, front then back, top/middle/bottom — it fits a phone
 *  without squeezing names to initials. Each row carries the code (what gets said out loud) AND the name (what
 *  you are looking for). Your own row reads "You"; luggage reads "Junk" so nobody hunts for a person who was
 *  never there; an empty sleeper is an em dash. An occupant we cannot name still reads as occupied ("Crew"). */
export function BunkChart({ v, nameOf, myId }: { v: VehicleView; nameOf: Map<string, string>; myId: string | null }) {
  return (
    <div className={styles.chart}>
      {SIDES.map((side) => {
        const onSide = v.bunks.filter((b) => bunkSide(b.slot) === side);
        if (onSide.length === 0) return null;
        return (
          <div className={styles.col} key={side}>
            <div className={styles.side}>{SIDE_LABEL[side]}</div>
            {GROUPS.map((group) => (
              <div className={styles.grp} key={group}>
                {TIERS.map((tier) => {
                  const b = onSide.find((x) => bunkGroup(x.slot) === group && bunkTier(x.slot) === tier);
                  if (!b) return null;
                  const isMe = !!myId && b.personId === myId;
                  const who = b.junk ? "Junk" : isMe ? "You" : b.personId ? (nameOf.get(b.personId) ?? "Crew") : "—";
                  return (
                    <div
                      key={tier}
                      className={styles.row}
                      data-me={isMe || undefined}
                      data-junk={b.junk || undefined}
                      data-empty={(!b.junk && !b.personId) || undefined}
                    >
                      <span className={styles.code}>{group}/{tier}</span>
                      <span className={styles.who}>{who}</span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
